import * as i0 from '@angular/core';
import { InjectionToken, PLATFORM_ID, Injectable, Inject, Optional, NgModule } from '@angular/core';
import { isPlatformServer, DOCUMENT } from '@angular/common';

/*!
 * Copyright © 2019. Verizon Connect Ireland Limited. All rights reserved.
 */
const COOKIES_TOKEN = new InjectionToken('COOKIES_TOKEN');

/*!
 * Copyright © 2019-2023. Verizon Connect Ireland Limited. All rights reserved.
 */
const MILLISECONDS_PER_MINUTE = 60000;
/**
 * Cookie Service.
 *
 * Provides utilities for working with cookies in the browser.
 *
 * Module: {@link FmCookieModule}.
 */
class FmCookieService {
    constructor(platformId, cookies, document) {
        this.platformId = platformId;
        this.cookies = cookies;
        this.document = document;
    }
    /**
     * Sets a cookie using the given name, value and expiry minutes.
     * @param cookieName The name to give the cookie.
     * @param cookieValue The value to give the cookie.
     * @param expiryMinutes The number of minutes before the cookie should expire.
     */
    setCookie(cookieName, cookieValue, expiryMinutes) {
        const date = new Date();
        const path = '/';
        date.setTime(date.getTime() + expiryMinutes * MILLISECONDS_PER_MINUTE);
        if (this.isServer()) {
            return;
        }
        this.document.cookie = `${cookieName}=${cookieValue};expires=${date.toUTCString()};path=${path};`;
    }
    /**
     * Retreives a cookie's value by the provided name.
     * @param cookieName The name of the cookie to retreive.
     * @returns The value of the cookie as a string.
     */
    getCookie(cookieName) {
        if (this.isServer()) {
            return null;
        }
        try {
            const cookies = this.document.cookie;
            const match = cookies.match(new RegExp('(^| |;)' + cookieName + '=([^;]+)'));
            if (match) {
                return decodeURIComponent(match[2]);
            }
        }
        catch (error) {
            console.error(error);
        }
        return '';
    }
    /**
     * Delete's a cookie by the provided name.
     * @param cookieName The name of the cookie to delete.
     */
    deleteCookie(cookieName) {
        if (this.isServer()) {
            return;
        }
        this.setCookie(cookieName, '', -1);
    }
    isServer() {
        return isPlatformServer(this.platformId);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: FmCookieService, deps: [{ token: PLATFORM_ID }, { token: COOKIES_TOKEN, optional: true }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: FmCookieService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: FmCookieService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [COOKIES_TOKEN]
                }] }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

/*!
 * Copyright © 2019-2023. Verizon Connect Ireland Limited. All rights reserved.
 */
/**
 * Cookie Module.
 *
 * Provides {@link FmCookieService}.
 */
class FmCookieModule {
    static forRoot() {
        return {
            ngModule: FmCookieModule,
            providers: [FmCookieService],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: FmCookieModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.8", ngImport: i0, type: FmCookieModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: FmCookieModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: FmCookieModule, decorators: [{
            type: NgModule,
            args: [{}]
        }] });

/*!
 * Copyright © 2023. Verizon Connect Ireland Limited. All rights reserved.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { COOKIES_TOKEN, FmCookieModule, FmCookieService };
